import ReactECharts from 'echarts-for-react';
import type { EChartsOption } from 'echarts';
import type { UserCompletionHistogramEntry } from '../../api/types';

interface Props {
  entries: UserCompletionHistogramEntry[];
  loading?: boolean;
}

const BUCKET_LABELS: Record<UserCompletionHistogramEntry['bucket'], string> = {
  '0%': '0%',
  '1-25%': '1–25%',
  '26-50%': '26–50%',
  '51-75%': '51–75%',
  '76-100%': '76–100%',
  no_data: 'No data',
};

// Red → amber → emerald gradient on the 0%..100% buckets so the chart
// reads as "stuck on the left, succeeding on the right". `no_data` uses
// a neutral grey to keep "absent" from looking like a score.
const BUCKET_COLORS: Record<UserCompletionHistogramEntry['bucket'], string> = {
  '0%': '#ef4444', // red 500
  '1-25%': '#f97316', // orange 500
  '26-50%': '#f59e0b', // amber 500
  '51-75%': '#84cc16', // lime 500
  '76-100%': '#10b981', // emerald 500
  no_data: '#d4d4dc', // zinc 200
};

export function CompletionRateHistogram({ entries, loading }: Props): JSX.Element {
  const totalClassified = entries
    .filter((e) => e.bucket !== 'no_data')
    .reduce((s, e) => s + e.users, 0);

  const option: EChartsOption = {
    grid: { left: 48, right: 24, top: 16, bottom: 56 },
    tooltip: {
      trigger: 'axis',
      axisPointer: { type: 'shadow' },
      formatter: (params) => {
        const items = Array.isArray(params) ? params : [params];
        const first = items[0];
        if (!first) return '';
        const idx = first.dataIndex as number;
        const entry = entries[idx];
        if (!entry) return '';
        if (entry.bucket === 'no_data') {
          return `<strong>${BUCKET_LABELS[entry.bucket]}</strong><br/>${entry.users.toLocaleString('en-US')} users (no v5 classification yet)`;
        }
        const pct =
          totalClassified > 0 ? ((entry.users / totalClassified) * 100).toFixed(1) : '0';
        return `<strong>${BUCKET_LABELS[entry.bucket]}</strong><br/>${entry.users.toLocaleString('en-US')} users (${pct}% of classified)`;
      },
    },
    xAxis: {
      type: 'category',
      data: entries.map((e) => BUCKET_LABELS[e.bucket]),
      name: 'Completion rate',
      nameLocation: 'middle',
      nameGap: 32,
      axisLabel: { fontSize: 12 },
    },
    yAxis: {
      type: 'value',
      name: 'Users',
      nameLocation: 'middle',
      nameGap: 36,
      minInterval: 1,
    },
    series: [
      {
        type: 'bar',
        barMaxWidth: 48,
        itemStyle: { borderRadius: [4, 4, 0, 0] },
        label: {
          show: true,
          position: 'top',
          formatter: (p) => (p.value as number).toLocaleString('en-US'),
          fontSize: 12,
        },
        data: entries.map((e) => ({
          value: e.users,
          itemStyle: { color: BUCKET_COLORS[e.bucket] },
        })),
      },
    ],
  };
  return (
    <ReactECharts
      option={option}
      style={{ height: 280, width: '100%' }}
      showLoading={loading}
      notMerge
      lazyUpdate
    />
  );
}
